import React from 'react';
import {
  Button, Container, FormikWrapper, FormRangeWithInputField, Message, TextualLink,
} from '@hvcgroep/design-system';
import { useTranslation } from 'react-i18next';
import { add, format } from 'date-fns';
import * as Yup from 'yup';
import { Form } from 'formik';
import Helmet from 'react-helmet';
import useContract from '../../hooks/useContract';
import SkeletonLoader from '../../components/SkeletonLoader';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import { DAYS_BEFORE_ANNUAL_COLLECTION_DATE_THRESHOLD, MAX_BUDGET_BILL_AMOUNT } from '../../config/constants';
import { useGetBudgetBillPlanQuery, usePostChangeMonthlyPaymentMutation } from '../../store/api-generated';

import './style.scss';
import CustomButtonLinkWrapper from '../../components/CustomButtonLinkWrapper';

const ChangeAdvancePayment: React.FC = () => {
  const {
    selectedContract,
    selectedAccount,
    selectedCustomer,
    canAdjustAdvancePayment,
  } = useContract();

  const {
    data: budgetBillPlan,
  } = useGetBudgetBillPlanQuery({
    accountId: selectedContract?.accountId!,
  }, {
    skip: !selectedContract?.accountId && !canAdjustAdvancePayment,
  });

  const [submit,
    {
      isLoading: isSubmitLoading,
      isSuccess: isSubmitSuccess,
      isError: isSubmitError,
      reset,
    }] = usePostChangeMonthlyPaymentMutation({
    fixedCacheKey: 'changeMonthlyPayment',
  });

  const { t } = useTranslation();

  const heading = t('pages.changeAdvancePayment.title');
  const siteTitle = t('general.siteTitle');

  const amendmentDeadline = React.useMemo(() => {
    const annualCollectionDate = selectedAccount?.financial?.annualCollectionDate && new Date(selectedAccount.financial.annualCollectionDate);

    if (!annualCollectionDate) {
      return null;
    }

    const now = new Date();

    return add(now, {
      days: DAYS_BEFORE_ANNUAL_COLLECTION_DATE_THRESHOLD,
    });
  }, [selectedAccount]);

  if (!canAdjustAdvancePayment && !!selectedContract) {
    return (
      <Container fluid>
        <ContractSwitcherHeader heading={t('pages.changeAdvancePayment.title')} />
        <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>
        <div className="row">
          <div className="col-md-6">
            <Message>{t('pages.changeAdvancePayment.cannotChangeAdvancePayment')}</Message>
            <Button href="/" CustomLinkComponent={CustomButtonLinkWrapper} variation="center" type="secondary">
              {t('pages.changeAdvancePayment.back')}
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  if (!selectedContract || !budgetBillPlan) {
    return (
      <Container fluid>
        <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

        <SkeletonLoader type="general" />
      </Container>
    );
  }

  const initialValues = {
    amount: budgetBillPlan.amount,
  };

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .min(budgetBillPlan.minAmount!, t('pages.changeAdvancePayment.error.min', { min: budgetBillPlan.minAmount }))
      .max(budgetBillPlan.maxAmount!, t('pages.changeAdvancePayment.error.max', { min: budgetBillPlan.maxAmount }))
      .required(t('pages.changeAdvancePayment.error.required')),
  });

  const onSubmit = (values: typeof initialValues) => {
    submit({
      monthlyPayment: {
        alternateId: selectedCustomer?.alternateId!,
        accountId: selectedAccount?.id!,
        accountNo: selectedAccount?.accountNumber!,
        nieuwTermijnBedrag: String(values.amount),
      },
    });
  };

  return (
    <Container fluid>
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>
      {isSubmitSuccess && (
        <Message
          type="status"
          actionButton={(
            <TextualLink
              onClick={() => {
                reset();
              }}
            >
              {t('messages.closeMessage')}
            </TextualLink>
          )}
        >
          <p>
            {t('pages.changeAdvancePayment.messages.success')}
          </p>
        </Message>
      )}
      {isSubmitError && (
        <Message
          type="error"
          actionButton={(
            <TextualLink
              onClick={() => {
                reset();
              }}
            >
              {t('messages.closeMessage')}
            </TextualLink>
          )}
        >
          <p>
            {t('pages.changeAdvancePayment.messages.error')}
          </p>
        </Message>
      )}
      <ContractSwitcherHeader heading={t('pages.changeAdvancePayment.title')} />
      <div className="row">
        <div className="col-md-6">
          <p>
            {t('pages.changeAdvancePayment.content')}
          </p>
          <FormikWrapper
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={onSubmit}
          >
            {({ setFieldValue }) => {
              return (
                <Form>
                  <FormRangeWithInputField
                    name="amount"
                    label={t('pages.changeAdvancePayment.label')}
                    horizontal={false}
                    unit=" &euro; "
                    visualMinMax
                    min={budgetBillPlan.minAmount}
                    max={budgetBillPlan.maxAmount}
                    accuracy={2}
                    description={amendmentDeadline ? t('pages.changeAdvancePayment.amendmentDeadline', {
                      budgetBillCounter: selectedAccount?.budgetBillCounter != null && MAX_BUDGET_BILL_AMOUNT - selectedAccount.budgetBillCounter,
                      deadline: format(amendmentDeadline, 'dd-MM-yyyy'),
                    }) : undefined}
                  />
                  <div className="mo-change-advance-payment__buttons">
                    <Button
                      variation="center"
                      type="secondary"
                      onClick={() => {
                        setFieldValue('amount', budgetBillPlan.amount);
                      }}
                    >
                      {t('pages.changeAdvancePayment.reset')}
                    </Button>
                    <Button
                      htmlType="submit"
                      variation="center"
                      isLoading={isSubmitLoading}
                    >
                      {t('pages.changeAdvancePayment.saveChanges')}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </FormikWrapper>
        </div>
      </div>
    </Container>
  );
};

export default ChangeAdvancePayment;
